import { isRitualBathFormChatEnabled } from "../context/formChatContexts";
import type { RitualBath } from "../types/ritualBath.types";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useRitualBathByIdQuery } from "./useRitualBathQueries";

const sameId = (left: unknown, right: unknown) =>
  left != null && right != null && String(left) === String(right);

/**
 * The chat edit seed is owner-scoped: personal profiles only reach their own
 * baths and terreiros only reach terreiro baths without a medium owner.
 */
export const isRitualBathInChatScope = (
  bath: Pick<RitualBath, "origin" | "terreiroId" | "mediumId"> | undefined,
  scope: { terreiroId?: string; mediumId?: string },
) => {
  if (!bath || bath.origin === "SYSTEM") return false;
  if (scope.terreiroId) {
    return sameId(bath.terreiroId, scope.terreiroId) && !bath.mediumId;
  }
  return sameId(bath.mediumId, scope.mediumId);
};

/** Whether the ritual bath chat replaces the classic wizard. */
export const useShouldUseRitualBathChat = ({
  bathId,
  enabled = true,
}: { bathId?: string; enabled?: boolean } = {}) => {
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isEligible =
    enabled && isRitualBathFormChatEnabled() && Boolean(terreiroId || mediumId);
  const bathQuery = useRitualBathByIdQuery({
    id: bathId,
    enabled: isEligible && Boolean(bathId),
  });

  if (!isEligible) return { shouldUseChat: false, isResolving: false };
  if (!bathId) return { shouldUseChat: true, isResolving: false };
  return {
    shouldUseChat: isRitualBathInChatScope(bathQuery.data, { terreiroId, mediumId }),
    isResolving: bathQuery.isLoading,
  };
};
